import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, FileText, Gift } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/rewards/me").then(({ data }) => setRewards(data)).catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  return (
    <>
      <Seo title="My Account | Just Peptides Supply" path="/account" noindex />
      <section className="mx-auto max-w-4xl px-5 lg:px-8 py-16">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl text-brand-navy tracking-tight">Hi, {user.name.split(" ")[0]}</h1>
            <p className="mt-1 text-brand-navy/55" data-testid="account-email">{user.email}</p>
          </div>
          <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full gap-2" data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>

        {rewards && rewards.enabled && (
          <div className="mt-10 rounded-2xl border border-brand-teal/25 bg-gradient-to-br from-brand-teal/10 to-transparent p-6" data-testid="rewards-card">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-brand-navy/50 flex items-center gap-1.5"><Gift className="h-4 w-4 text-brand-teal" /> Your Rewards Credit</p>
                <p className="mt-1 font-display text-4xl text-brand-teal" data-testid="rewards-balance">${rewards.balance.toFixed(2)}</p>
              </div>
              <div className="text-right max-w-[220px]">
                <p className="text-brand-navy/55 text-sm">Earn ${rewards.earn_reward.toFixed(2)} back for every ${rewards.earn_spend.toFixed(2)} spent</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-brand-navy/60">
              {rewards.can_redeem ? "Apply your rewards credit at checkout for money off your order." : `Reach $${rewards.min_redeem.toFixed(2)} in rewards to start redeeming.`}
            </p>
            {rewards.history && rewards.history.length > 0 && (
              <div className="mt-4 border-t border-brand-navy/10 pt-4 space-y-2" data-testid="rewards-history">
                {rewards.history.slice(0, 6).map((h) => (
                  <div key={h.id} className="flex justify-between text-sm">
                    <span className="text-brand-navy/60">{h.note} · {new Date(h.created_at).toLocaleDateString()}</span>
                    <span className={`font-semibold ${h.amount >= 0 ? "text-brand-teal" : "text-brand-navy/60"}`}>{h.amount >= 0 ? "+" : "−"}${Math.abs(h.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <h2 className="mt-12 font-display text-2xl text-brand-navy mb-5 flex items-center gap-2">
          <Package className="h-5 w-5 text-brand-blue" /> Order History
        </h2>

        {orders.length === 0 ? (
          <div className="rounded-xl border border-brand-navy/10 bg-brand-mist p-10 text-center text-brand-navy/55" data-testid="no-orders">
            No orders yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="rounded-xl border border-brand-navy/10 bg-white p-5" data-testid={`order-${o.id}`}>
                <div className="flex justify-between items-center">
                  <span className="font-mono-label text-xs uppercase tracking-[0.12em] text-brand-navy/50">
                    {new Date(o.created_at).toLocaleDateString()}
                  </span>
                  <span className="font-mono-label text-[11px] uppercase tracking-[0.12em] bg-brand-teal/15 text-brand-teal px-2.5 py-1 rounded-full">{o.status}</span>
                </div>
                <div className="mt-3 space-y-1 text-sm text-brand-navy/70">
                  {o.items.map((it) => (
                    <div key={it.slug} className="flex justify-between">
                      <span>{it.name} × {it.quantity}</span>
                      <span>${(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-brand-navy/10 flex justify-between items-center font-semibold text-brand-navy">
                  <span>Total</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => window.open(`${API}/orders/${o.id}/receipt`, "_blank")} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-blue hover:text-brand-navy transition-colors" data-testid={`download-receipt-${o.id}`}><FileText className="h-4 w-4" /> Receipt</button>
                    <span>${o.amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
